<template>
    <div class="wrap">
        <SectionTitle text="New Arrival" class="title" />

        <ul class="newArrival">
            <li v-for="(item, index) in visibleNewArrival" :key="index">
                <a :href="switchItemURL(item)" v-on="canHover ? hoverHandlers(item, index) : {}" ref="items">
                    <img :src="thumbSrc(item)" loading="lazy" :data-first-thumb="item.first_thumb || '0'" :data-second-thumb="item.second_thumb || '00'" alt="" class="img" v-show="!item.gif" ref="itemImages" />

                    <img :src="`https://thumbnail.image.rakuten.co.jp/@0_mall/auc-rodeo/cabinet/${item.image}-0.gif`" loading="lazy" alt="" class="img" v-if="item.gif" />

                    <div class="brand">{{ item.brand }}</div>
                    <div class="price">{{ taxIn(item.price) }}</div>
                </a>
            </li>
        </ul>

        <div class="infinite-sentinel" ref="sentinel" v-show="hasMore"></div>
    </div>
</template>

<script>
import SectionTitle from '@/components/SectionTitle.vue'

export default {
  name: 'NewArrivalAll',

  components: { SectionTitle },

  data () {
    return {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1524, // 表示件数計算用の現在幅（将来拡張用）

      initialCount: 40, // 初期表示件数
      stepCount: 40, // 追加読み込み件数
      visibleCount: 40, // 現在表示している件数
      isLoadingMore: false, // 追加読み込み中フラグ（多重実行防止）

      intersectionObserver: null, // 無限スクロール用 Observer
      scrollFallbackHandler: null, // Observer未対応時のスクロール監視
      onResizeHandler: null, // resize監視ハンドラ

      canHover: false, // PC等ホバー可能端末のみ true
      hoverMql: null, // hover可否判定用 MediaQueryList
      onHoverChange: null // hover可否変更時のコールバック
    }
  },

  computed: {
    // NEWグループのみ抽出
    newArrival () {
      const newJson = this.$store.state.json.new
      return newJson.filter(item => item.group === 'NEW')
    },

    // 表示中の範囲だけ切り出し
    visibleNewArrival () {
      return this.newArrival.slice(0, this.visibleCount)
    },

    // 追加読み込みが必要か
    hasMore () {
      return this.visibleCount < this.newArrival.length
    }
  },

  methods: {
    // 無限スクロール初期化（Observer優先・無ければscroll監視）
    initInfiniteScroll () {
      if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          const entry = entries[0]
          if (!entry || !entry.isIntersecting) return
          this.loadMore()
        }, {
          root: null,
          rootMargin: '200px 0px',
          threshold: 0
        })

        if (this.$refs.sentinel) this.intersectionObserver.observe(this.$refs.sentinel)
        return
      }

      this.scrollFallbackHandler = () => {
        if (!this.hasMore || this.isLoadingMore) return
        const bottom = window.innerHeight + window.scrollY
        const height = document.documentElement.scrollHeight
        if (bottom >= height - 200) this.loadMore()
      }
      window.addEventListener('scroll', this.scrollFallbackHandler, { passive: true })
    },

    // 追加読み込み（件数を増やす）
    loadMore () {
      if (!this.hasMore || this.isLoadingMore) return
      this.isLoadingMore = true

      window.requestAnimationFrame(() => {
        const next = Math.min(this.visibleCount + this.stepCount, this.newArrival.length)
        this.visibleCount = next
        this.isLoadingMore = false
      })
    },

    // 初期表示用サムネURL（first_thumb優先）
    thumbSrc (item) {
      const first = item.first_thumb || '0'
      return `https://thumbnail.image.rakuten.co.jp/@0_mall/auc-rodeo/cabinet/${item.image}-${first}.jpg`
    },

    // 1枚目/2枚目サムネURL生成
    thumbUrl (item, which) {
      const first = item.first_thumb || '0'
      const second = item.second_thumb || '00'
      const thumb = (which === 'second') ? second : first
      return `https://thumbnail.image.rakuten.co.jp/@0_mall/auc-rodeo/cabinet/${item.image}-${thumb}.jpg`
    },

    // hover可能時のみ mouseenter/leave を付与
    hoverHandlers (item, index) {
      return {
        mouseenter: () => this.itemMouseOver(item, index),
        mouseleave: () => this.itemMouseOut(item, index)
      }
    },

    // ホバーで2枚目へ切替（GIF/スマホは対象外）
    itemMouseOver (item, index) {
      if (!this.canHover || item.gif) return
      const imgDom = this.$refs.itemImages && this.$refs.itemImages[index]
      if (!imgDom) return
      imgDom.src = this.thumbUrl(item, 'second')
    },

    // ホバー解除で1枚目へ戻す
    itemMouseOut (item, index) {
      if (!this.canHover || item.gif) return
      const imgDom = this.$refs.itemImages && this.$refs.itemImages[index]
      if (!imgDom) return
      imgDom.src = this.thumbUrl(item, 'first')
    },

    // hover可能端末か判定（スマホでのhover誤動作を回避）
    updateCanHover () {
      this.canHover = !!(
        window.matchMedia &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches
      )
    }
  },

  mounted () {
    this.onResizeHandler = () => { this.windowWidth = window.innerWidth } // 画面幅変更を監視
    window.addEventListener('resize', this.onResizeHandler, { passive: true })

    this.onHoverChange = () => this.updateCanHover() // hover可否の変化に追従
    this.updateCanHover()
    if (window.matchMedia) {
      this.hoverMql = window.matchMedia('(hover: hover) and (pointer: fine)') // hover判定の監視
      if (this.hoverMql.addEventListener) this.hoverMql.addEventListener('change', this.onHoverChange)
      else if (this.hoverMql.addListener) this.hoverMql.addListener(this.onHoverChange)
    }

    this.visibleCount = this.initialCount // 初期表示件数を適用
    this.initInfiniteScroll() // 無限スクロール開始
  },

  beforeDestroy () {
    if (this.onResizeHandler) { // resize監視解除
      window.removeEventListener('resize', this.onResizeHandler)
      this.onResizeHandler = null
    }

    if (this.scrollFallbackHandler) { // scroll監視解除（フォールバック）
      window.removeEventListener('scroll', this.scrollFallbackHandler)
      this.scrollFallbackHandler = null
    }

    if (this.intersectionObserver) { // Observer解除
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }

    if (this.hoverMql) { // hover監視解除
      if (this.hoverMql.removeEventListener) this.hoverMql.removeEventListener('change', this.onHoverChange)
      else if (this.hoverMql.removeListener) this.hoverMql.removeListener(this.onHoverChange)
      this.hoverMql = null
    }
    this.onHoverChange = null // 参照クリア
  }
}
</script>

<style lang="scss" scoped>
.newArrival {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    text-transform: uppercase;
    border-top: 1px solid #dddddd;
    border-left: 1px solid #dddddd;

    @media screen and (max-width: 722px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: 723px) and (max-width: 963px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media screen and (min-width: 964px) and (max-width: 1523px) {
        grid-template-columns: repeat(4, 1fr);
    }

    @media screen and (min-width: 1524px) and (max-width: 2286px) {
        grid-template-columns: repeat(5, 1fr);
    }

    @media screen and (min-width: 2287px) {
        grid-template-columns: repeat(5, 1fr);
    }

    >li {
        padding: 30px;
        font-size: 13px;
        text-align: left;
        border-right: 1px solid #dddddd;
        border-bottom: 1px solid #dddddd;

        @media screen and (max-width: 722px) {
            padding: 15px;
            padding: 15px 15px 23px;
        }
    }

    .img {
        width: 100%;
    }

    .brand {
        padding: 14px 0 6px;

        @media screen and (max-width: 722px) {
            padding: 12px 0 6px;
            padding: 11px 0 4px;
            padding: 10px 0 4px;
        }
    }
}

.infinite-sentinel {
    width: 100%;
    height: 1px;
}
</style>
